import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import MetricsCard from '../../components/Shared/MetricsCard';
import AIInsightsGenerator from '../../components/Dashboard/AIInsightsGenerator';
import { teacherNavItems } from '../../constants/navigation';

/**
 * StudentDetailPage - Detailed view of a single student for teachers
 * Shows student profile, engagement metrics, activity timeline, AI insights,
 * actions (edit, transfer, remove, reset password), and chat history with filters
 */
const StudentDetailPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);

  // Chat history
  const [chatHistory, setChatHistory] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSearch, setChatSearch] = useState('');
  const [chatDateFrom, setChatDateFrom] = useState('');
  const [chatDateTo, setChatDateTo] = useState('');
  const [expandedSessions, setExpandedSessions] = useState(new Set());

  const fetchStudentData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest(`/api/teacher/student/${studentId}`, 'GET');
      setStudentData(data);
    } catch (err) {
      console.error('Failed to load student details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchStudentData();
  }, [fetchStudentData]);

  // Fetch chat history
  const fetchChatHistory = useCallback(async () => {
    try {
      setChatLoading(true);
      let url = `/api/teacher/chat-history/student/${studentId}?limit=20`;
      if (chatSearch) url += `&search=${encodeURIComponent(chatSearch)}`;
      if (chatDateFrom) url += `&date_from=${chatDateFrom}`;
      if (chatDateTo) url += `&date_to=${chatDateTo}`;
      const data = await apiRequest(url, 'GET');
      setChatHistory(data);
    } catch (err) {
      console.error('Failed to load chat history:', err);
    } finally {
      setChatLoading(false);
    }
  }, [studentId, chatSearch, chatDateFrom, chatDateTo]);

  useEffect(() => {
    fetchChatHistory();
  }, [fetchChatHistory]);

  const toggleSessionExpanded = (sessionId) => {
    const next = new Set(expandedSessions);
    if (next.has(sessionId)) next.delete(sessionId);
    else next.add(sessionId);
    setExpandedSessions(next);
  };

  if (loading) {
    return (
      <DashboardLayout title="Student Details" navItems={teacherNavItems} userRole={currentUser?.user_type || 'teacher'} userName={currentUser?.full_name || 'Teacher'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading student details...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Student Details" navItems={teacherNavItems} userRole={currentUser?.user_type || 'teacher'} userName={currentUser?.full_name || 'Teacher'}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading student details: {error}
        </div>
        <button onClick={() => navigate('/teacher/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
          Back to Dashboard
        </button>
      </DashboardLayout>
    );
  }

  const student = studentData?.student;
  const metrics = studentData?.engagement_metrics || {};

  return (
    <DashboardLayout title="Student Details" navItems={teacherNavItems} userRole={currentUser?.user_type || 'teacher'} userName={currentUser?.full_name || 'Teacher'}>
      {/* Back Button */}
      <div className="mb-6">
        <button onClick={() => navigate('/teacher/dashboard')} className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center">
          &larr; Back to Dashboard
        </button>
      </div>

      {/* Student Header with Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">
              {student?.first_name} {student?.last_name}
            </h2>
            <p className="text-gray-600 mt-1">@{student?.username}</p>
            <p className="text-gray-500 text-sm mt-2">
              Grade: <span className="capitalize">{student?.grade_level || 'N/A'}</span>
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-right mr-4">
              <p className="text-sm text-gray-600">Student since</p>
              <p className="text-lg font-semibold text-gray-900">
                {student?.created_at ? new Date(student.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            {/* Actions Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Actions &#9662;
              </button>
              {showActionsDropdown && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                  <button onClick={() => { setShowEditModal(true); setShowActionsDropdown(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Edit Info</button>
                  <button onClick={() => { setShowTransferModal(true); setShowActionsDropdown(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Transfer Class</button>
                  <button onClick={() => { setShowRemoveModal(true); setShowActionsDropdown(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 text-red-600">Remove from Class</button>
                  <button onClick={() => { setShowResetModal(true); setShowActionsDropdown(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Reset Password</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricsCard title="Total Sessions" value={metrics.sessions_count || 0} subtitle="all time" icon={'\uD83D\uDCAC'} />
        <MetricsCard title="Messages Sent" value={metrics.messages_sent || 0} subtitle="total messages" icon={'\u2709\uFE0F'} />
        <MetricsCard title="Images Uploaded" value={metrics.images_uploaded || 0} subtitle="for help" icon={'\uD83D\uDDBC\uFE0F'} />
        <MetricsCard title="Current Streak" value={`${metrics.streak_days || 0} days`} subtitle="consecutive days" icon={'\uD83D\uDD25'} />
      </div>

      {/* AI Insights Generator */}
      <div className="mb-8">
        <AIInsightsGenerator
          studentId={studentId}
          studentName={`${student?.first_name} ${student?.last_name}` || student?.username}
        />
      </div>

      {/* Topics Explored */}
      {studentData?.topics_explored && studentData.topics_explored.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Topics Explored</h3>
          <div className="space-y-3">
            {studentData.topics_explored.map((topic, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl font-bold text-gray-300">{index + 1}</span>
                  <span className="text-gray-900 font-medium">{topic.topic}</span>
                </div>
                <span className="text-sm text-gray-500">{topic.sessions} sessions</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Timeline */}
      {studentData?.activity_timeline && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Timeline (Last 30 Days)</h3>
          <div className="flex items-end space-x-1 h-32">
            {studentData.activity_timeline.map((day, index) => {
              const maxSessions = Math.max(...studentData.activity_timeline.map(d => d.sessions), 1);
              const height = (day.sessions / maxSessions) * 100;
              return (
                <div
                  key={index}
                  className="flex-1 bg-indigo-200 hover:bg-indigo-400 rounded-t transition-all relative group"
                  style={{ height: `${height}%`, minHeight: day.sessions > 0 ? '8px' : '2px' }}
                  title={`${day.date}: ${day.sessions} sessions`}
                >
                  <div className="hidden group-hover:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    {day.date}: {day.sessions} sessions
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">Hover over bars to see details</p>
        </div>
      )}

      {/* Chat History Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Chat History</h3>

        {/* Filter Bar */}
        <div className="flex flex-wrap gap-3 mb-4">
          <input
            type="text"
            value={chatSearch}
            onChange={(e) => setChatSearch(e.target.value)}
            placeholder="Search messages..."
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex-1 min-w-[200px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <input
            type="date"
            value={chatDateFrom}
            onChange={(e) => setChatDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            type="date"
            value={chatDateTo}
            onChange={(e) => setChatDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {chatLoading ? (
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            <span className="ml-2 text-sm text-gray-600">Loading...</span>
          </div>
        ) : !chatHistory?.sessions?.length ? (
          <p className="text-gray-500 text-center py-4">No chat sessions found</p>
        ) : (
          <div className="space-y-2">
            {chatHistory.sessions.map((session) => (
              <div key={session.session_id} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleSessionExpanded(session.session_id)}
                  className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{session.title}</p>
                    <p className="text-xs text-gray-500">
                      {session.created_at ? new Date(session.created_at).toLocaleDateString() : ''} &middot; {session.message_count} messages
                    </p>
                  </div>
                  <span className="text-gray-400">{expandedSessions.has(session.session_id) ? '\u25B2' : '\u25BC'}</span>
                </button>
                {expandedSessions.has(session.session_id) && (
                  <div className="border-t border-gray-200 px-4 py-3 max-h-64 overflow-y-auto bg-gray-50">
                    {session.messages.map((msg) => (
                      <div key={msg.id} className={`mb-2 flex ${msg.sender === 'student' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                          msg.sender === 'student' ? 'bg-indigo-100 text-indigo-900' : 'bg-white border border-gray-200 text-gray-800'
                        }`}>
                          <p>{msg.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ====== MODALS ====== */}

      {/* Edit Info Modal */}
      {showEditModal && (
        <EditStudentModal
          student={student}
          studentId={studentId}
          onClose={() => setShowEditModal(false)}
          onSaved={fetchStudentData}
        />
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <TransferStudentModal
          studentId={studentId}
          onClose={() => setShowTransferModal(false)}
          onSaved={fetchStudentData}
        />
      )}

      {/* Remove from Class Modal */}
      {showRemoveModal && (
        <RemoveStudentModal
          studentId={studentId}
          onClose={() => setShowRemoveModal(false)}
          onSaved={fetchStudentData}
        />
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <ResetPasswordModal
          studentId={studentId}
          studentUsername={student?.username}
          onClose={() => setShowResetModal(false)}
        />
      )}
    </DashboardLayout>
  );
};

// ============================================================================
// MODALS
// ============================================================================

const EditStudentModal = ({ student, studentId, onClose, onSaved }) => {
  const [form, setForm] = useState({
    first_name: student?.first_name || '',
    last_name: student?.last_name || '',
    grade_level: student?.grade_level || 'elementary',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiRequest(`/api/teacher/student/${studentId}`, 'PUT', form);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} title="Edit Student Info">
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
          <input type="text" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
          <input type="text" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
          <select value={form.grade_level} onChange={(e) => setForm({ ...form, grade_level: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="elementary">Elementary</option>
            <option value="middle">Middle</option>
          </select>
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">Cancel</button>
      </div>
    </ModalOverlay>
  );
};

const TransferStudentModal = ({ studentId, onClose, onSaved }) => {
  const [classSections, setClassSections] = useState([]);
  const [targetId, setTargetId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await apiRequest('/api/teacher/dashboard', 'GET');
        setClassSections(data.class_sections || []);
      } catch (err) { /* ignore */ }
    };
    fetch();
  }, []);

  const handleTransfer = async () => {
    if (!targetId) return;
    try {
      setSaving(true);
      await apiRequest(`/api/teacher/student/${studentId}/transfer`, 'PUT', {
        target_class_section_id: parseInt(targetId, 10)
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} title="Transfer Student">
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      <p className="text-sm text-gray-600 mb-3">Select the class section to transfer this student to:</p>
      <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4">
        <option value="">Select a class...</option>
        {classSections.map(s => (
          <option key={s.id} value={s.id}>
            {s.period_number ? `Period ${s.period_number} - ` : ''}{s.section_name}
          </option>
        ))}
      </select>
      <div className="flex gap-3">
        <button onClick={handleTransfer} disabled={saving || !targetId} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium">
          {saving ? 'Transferring...' : 'Transfer'}
        </button>
        <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">Cancel</button>
      </div>
    </ModalOverlay>
  );
};

const RemoveStudentModal = ({ studentId, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // We'll try to get enrollments from the student detail - for now, allow removal
    // The backend handles validation
  }, []);

  const handleRemove = async (enrollmentId) => {
    try {
      setSaving(true);
      await apiRequest(`/api/teacher/student/${studentId}/enrollment/${enrollmentId}`, 'DELETE');
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} title="Remove from Class">
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      <p className="text-sm text-gray-600 mb-4">
        This will remove the student from their current class section. The student account will not be deleted.
      </p>
      <p className="text-xs text-gray-500 mb-4">
        Note: Enter the enrollment ID to remove. You can find enrollment IDs in the class management page.
      </p>
      <EnrollmentRemover studentId={studentId} onRemove={handleRemove} saving={saving} />
      <div className="flex gap-3 mt-4">
        <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">Close</button>
      </div>
    </ModalOverlay>
  );
};

const EnrollmentRemover = ({ studentId, onRemove, saving }) => {
  const [enrollmentId, setEnrollmentId] = useState('');

  return (
    <div className="flex gap-2">
      <input
        type="number"
        value={enrollmentId}
        onChange={(e) => setEnrollmentId(e.target.value)}
        placeholder="Enrollment ID"
        className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex-1"
      />
      <button
        onClick={() => onRemove(parseInt(enrollmentId, 10))}
        disabled={saving || !enrollmentId}
        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
      >
        Remove
      </button>
    </div>
  );
};

const ResetPasswordModal = ({ studentId, studentUsername, onClose }) => {
  const [tempPassword, setTempPassword] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleReset = async () => {
    try {
      setSaving(true);
      const result = await apiRequest(`/api/teacher/student/${studentId}/reset-password`, 'POST');
      setTempPassword(result.temporary_password);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} title="Reset Student Password">
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      {!tempPassword ? (
        <>
          <p className="text-sm text-gray-600 mb-4">
            This will generate a temporary password for <strong>@{studentUsername}</strong>. The student will use this to log in and should change it immediately.
          </p>
          <div className="flex gap-3">
            <button onClick={handleReset} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium">
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800 font-medium mb-2">Password reset successful!</p>
            <p className="text-sm text-gray-700">Temporary password:</p>
            <p className="font-mono text-lg font-bold text-gray-900 mt-1">{tempPassword}</p>
          </div>
          <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            This password will only be shown once. Please share it with the student securely.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => { navigator.clipboard.writeText(tempPassword); }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
            >
              Copy to Clipboard
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">Done</button>
          </div>
        </>
      )}
    </ModalOverlay>
  );
};

/** Shared modal overlay */
const ModalOverlay = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
      </div>
      {children}
    </div>
  </div>
);

export default StudentDetailPage;
